import { Col, DatePicker, Form, FormInstance, Input, Row, Select } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useAuth } from 'ict24h-themes-react'
import {
  CreateProjectInput,
  ProjectEntity,
  UpdateProjectInput,
} from '../../../../../../server-api-hrm.type'
import { dateFormatShort } from '../../../../../../utils/dateFormat'
import datePickerDisabledDate from '../../../../../../utils/date-picker-disabled-date'
import useGetDataCreateDeptProjectPermission from '../../../create-dept-project-permission/hooks/use-get-data-create-dept-project-permission'
import uniqueArrayById from '../../../../../../utils/unique-array-by-id'
import useGetCurrentUser from '../../../../../cau-hinh-thong-tin/modules/user/hooks/use-get-current-user'
import SearchSelect from '../../../../components/re-useable/search-select-v3/SearchSelect'
import useHandleSelectData from '../../../../components/re-useable/search-select-v3/use-handle-select-data'
import { getListProject } from '../../project.service'

type PropsType = { showDepartment: boolean; isTemplate: boolean } & (
  | {
      form: FormInstance<CreateProjectInput>
      data?: undefined
    }
  | {
      form: FormInstance<UpdateProjectInput>
      data: ProjectEntity
    }
)

const FormItemsProject = ({
  showDepartment,
  isTemplate,
  form,
  data,
}: PropsType) => {
  const { token } = useAuth()
  const startDate = Form.useWatch('startDate', form)
  const endDate = Form.useWatch('endDate', form)

  const startD = startDate ? dayjs(startDate) : null
  const endD = endDate ? dayjs(endDate) : null

  const {
    currentUser,
    getCurrentUser,
    loading: loadingUser,
  } = useGetCurrentUser({
    customRequestHandler: true,
  })

  const {
    data: permitDepts,
    getData: getPermitDepts,
    loading: loadingPermitDep,
  } = useGetDataCreateDeptProjectPermission({
    autoGetFullData: true,
    filter: { userIds: currentUser ? [currentUser.id] : [] },
  })

  const permitDeptOptions =
    data && data.department
      ? uniqueArrayById([
          data.department,
          ...permitDepts.map((item) => item.department).filter(Boolean),
        ])
      : permitDepts.map((item) => item.department).filter(Boolean)

  const handleGetPermitDepts = () => {
    if (!showDepartment || permitDepts.length >= 1) return
    if (!currentUser) {
      getCurrentUser({
        onFinish: (user) => {
          if (user)
            getPermitDepts({
              type: 'fetch',
              localHandler: { filter: { userIds: [user.id] } },
            })
        },
      })
      return
    }
    getPermitDepts({ type: 'fetch' })
  }

  const templateSelectData = useHandleSelectData({
    getListDataFunction: async (query) =>
      getListProject(token, 'all', {
        ...query,
        filter: { ...query?.filter, isTemplate: true },
      }),
    fetchOnFocus: true,
  })

  return (
    <Row gutter={[12, 0]}>
      <Col span={24}>
        <Form.Item
          name="title"
          label="Tiêu đề"
          rules={[{ required: true, message: 'Bạn chưa nhập: Tiêu đề' }]}
        >
          <Input.TextArea
            placeholder="Nhập tiêu đề"
            autoSize={{ minRows: 1, maxRows: 4 }}
          />
        </Form.Item>
      </Col>

      {showDepartment ? (
        <Col span={24}>
          <Form.Item
            name="departmentId"
            label="Phòng ban"
            rules={[{ required: true, message: 'Bạn chưa chọn: Phòng ban' }]}
          >
            <Select
              placeholder="Chọn phòng ban"
              disabled={!!data}
              options={permitDeptOptions.map((dept) => ({
                value: dept.id,
                label: dept.name,
              }))}
              style={{ width: '100%' }}
              loading={loadingPermitDep || loadingUser}
              onDropdownVisibleChange={(open) => {
                if (open) handleGetPermitDepts()
              }}
              showSearch={false}
            />
          </Form.Item>
        </Col>
      ) : null}

      <Col xs={24} sm={isTemplate ? 24 : 12}>
        <Form.Item
          name="startDate"
          label="Ngày bắt đầu"
          getValueProps={(value: string | null | undefined) => ({
            value: value ? dayjs(value) : null,
          })}
          normalize={(value: Dayjs | null | undefined) =>
            value && value.toISOString()
          }
        >
          <DatePicker
            placeholder="Chọn ngày"
            picker="date"
            format={dateFormatShort}
            showNow
            style={{ width: '100%' }}
            disabledDate={datePickerDisabledDate(endD, 'start', 'day')}
            defaultPickerValue={startD || endD || undefined}
          />
        </Form.Item>
      </Col>

      {!isTemplate ? (
        <Col xs={24} sm={12}>
          <Form.Item
            name="endDate"
            label="Ngày kết thúc"
            getValueProps={(value: string | null | undefined) => ({
              value: value ? dayjs(value) : null,
            })}
            normalize={(value: Dayjs | null | undefined) =>
              value && value.toISOString()
            }
          >
            <DatePicker
              placeholder="Chọn ngày"
              picker="date"
              format={dateFormatShort}
              showNow
              style={{ width: '100%' }}
              disabledDate={datePickerDisabledDate(startD, 'end', 'day')}
              defaultPickerValue={endD || startD || undefined}
            />
          </Form.Item>
        </Col>
      ) : null}

      {!data && !isTemplate ? (
        <SearchSelect
          dataHandleResult={templateSelectData}
          labelRender={(rec) => rec.title}
          formItemName="templateId"
          placeHolder="Chọn dự án mẫu để sao chép"
          formItemProps={{
            label: 'Sao chép từ dự án mẫu',
          }}
        />
      ) : null}

      <Col span={24}>
        <Form.Item name="description" label="Mô tả">
          <Input.TextArea
            placeholder="Nhập mô tả"
            autoSize={{ minRows: 2, maxRows: 6 }}
          />
        </Form.Item>
      </Col>
    </Row>
  )
}

export default FormItemsProject
